import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, NavLink, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { GoogleMap, LoadScript, Polygon, Marker } from '@react-google-maps/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DeliveryZoneMap from '../components/map/DeliveryZoneMap';
import {
  LayoutDashboard, Briefcase, FileText, MapPin, PlusCircle,
  LogOut, Package, Loader2, Clock, CheckCircle, AlertCircle,
  ChevronRight, Eye, Calendar, Send, Menu, X, Pencil, Trash2, Search, Shield, ExternalLink,
  TrendingUp, AlertTriangle, Mail
} from 'lucide-react';
import DailyCcEditor from '../components/DailyCcEditor';
import JobReportView from '../components/JobReportView';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const getClientAuth = () => {
  const token = localStorage.getItem('client_token');
  if (!token) return null;
  return { headers: { Authorization: `Bearer ${token}` } };
};

// Status badge helper
const StatusBadge = ({ status }) => {
  const styles = {
    open: 'bg-blue-100 text-blue-700',
    assigned: 'bg-cyan-100 text-cyan-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    paid: 'bg-emerald-100 text-emerald-700',
    pending_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-600',
    pending: 'bg-yellow-100 text-yellow-700',
    reviewed: 'bg-blue-100 text-blue-700',
  };
  const label = (status || '').replace(/_/g, ' ');
  return <Badge className={`${styles[status] || 'bg-gray-100 text-gray-600'} capitalize text-xs`}>{label}</Badge>;
};

// ==================== DASHBOARD ====================
const ClientDashboardHome = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/client/dashboard`, getClientAuth());
        setData(res.data);
      } catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-purple-500" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 border-0 shadow-sm">
          <Package size={20} className="text-purple-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{data.total_jobs}</p>
          <p className="text-xs text-gray-500">Total Jobs</p>
        </Card>
        <Card className="p-5 border-0 shadow-sm">
          <Clock size={20} className="text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{data.active_jobs}</p>
          <p className="text-xs text-gray-500">Active Jobs</p>
        </Card>
        <Card className="p-5 border-0 shadow-sm">
          <CheckCircle size={20} className="text-green-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{data.completed_jobs}</p>
          <p className="text-xs text-gray-500">Completed</p>
        </Card>
      </div>

      {data.pending_requests > 0 && (
        <Card className="p-4 border-0 shadow-sm bg-amber-50 border-l-4 border-l-amber-400">
          <p className="text-sm text-amber-800">You have <span className="font-bold">{data.pending_requests}</span> pending job request(s).</p>
        </Card>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Jobs</h2>
        {data.recent_jobs.length > 0 ? (
          <div className="space-y-2">
            {data.recent_jobs.map(job => (
              <Card key={job.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{job.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge status={job.status} />
                      {job.location && <span className="text-xs text-gray-400">{job.location}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">{job.deliveries || 0}</p>
                    <p className="text-xs text-gray-400">deliveries</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 border-0 shadow-sm text-center">
            <Package size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">No jobs yet</p>
          </Card>
        )}
      </div>
    </div>
  );
};

// ==================== JOBS LIST ====================
const ClientJobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetchJobs = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/api/client/jobs${params}`, getClientAuth());
      // Mirror the Reports tab: only surface customer-relevant statuses.
      // pending_approval / rejected / paid / archived are admin-side states
      // and should never appear on the client portal. The 'All' tab needs
      // the explicit filter; the named tabs already constrain via ?status=.
      const REPORTABLE_STATUSES = new Set(['open', 'in_progress', 'completed']);
      const all = Array.isArray(res.data) ? res.data : [];
      setJobs(filter === 'all'
        ? all.filter((j) => REPORTABLE_STATUSES.has((j.status || '').toLowerCase()))
        : all);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'in_progress', label: 'Active' },
    { key: 'completed', label: 'Completed' },
    { key: 'open', label: 'Open' },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">My Jobs</h1>

      <div className="flex gap-2">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setLoading(true); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 size={24} className="animate-spin text-purple-500" /></div>
      ) : jobs.length > 0 ? (
        <div className="space-y-2">
          {jobs.map(job => (
            <Card
              key={job.id}
              className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/client/jobs/${job.id}`)}
              data-testid={`client-job-${job.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{job.title}</p>
                    {job.job_number && <span className="text-xs text-gray-400">#{String(job.job_number).padStart(4, '0')}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={job.status} />
                    {job.location && <span className="text-xs text-gray-400 truncate">{job.location}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">{job.deliveries || 0}</p>
                    <p className="text-xs text-gray-400">deliveries</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-300" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 border-0 shadow-sm text-center">
          <Briefcase size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No jobs found</p>
        </Card>
      )}
    </div>
  );
};

// ==================== JOB DETAIL ====================
const ClientJobDetail = () => {
  const jobId = window.location.pathname.split('/').pop();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/client/jobs/${jobId}`, getClientAuth());
        setJob(res.data);
      } catch { toast.error('Failed to load job details'); navigate('/client/jobs'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [jobId, navigate]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-purple-500" /></div>;
  if (!job) return null;

  const progressPercent = job.deliveries > 0 ? Math.min(100, Math.round((job.delivered_count / job.deliveries) * 100)) : 0;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/client/jobs')} className="text-purple-600 hover:text-purple-700 text-sm font-medium">&larr; Back to Jobs</button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={job.status} />
            {job.location && <span className="text-sm text-gray-500">{job.location}</span>}
          </div>
        </div>
        {job.has_tracking && ['in_progress', 'assigned'].includes(job.status) && (
          <Button size="sm" onClick={() => window.open(`/track/${job.id}`, '_blank')} className="bg-purple-600 hover:bg-purple-500">
            <Eye size={14} className="mr-1" /> Live Track
          </Button>
        )}
      </div>

      {/* Progress */}
      <Card className="p-5 border-0 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">Delivery Progress</p>
          <p className="text-sm font-bold text-purple-600">{job.delivered_count} / {job.deliveries}</p>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div className="bg-purple-600 h-3 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-1">{progressPercent}% complete</p>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-xl font-bold text-gray-900">{job.deliveries || 0}</p>
          <p className="text-xs text-gray-500">Total Deliveries</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-xl font-bold text-gray-900">{job.photo_count || 0}</p>
          <p className="text-xs text-gray-500">Photos</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-xl font-bold text-gray-900">{job.delivery_type || 'Standard'}</p>
          <p className="text-xs text-gray-500">Type</p>
        </Card>
      </div>

      {/* Tracking Link */}
      {job.delivery_boundary && job.delivery_boundary.length > 0 && (
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-purple-500" />
              <p className="font-medium text-gray-900">Delivery Zone Map</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => window.open(`/track/${job.id}`, '_blank')}>
              <Eye size={14} className="mr-1" /> View Map
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

// ==================== GPS TRACKING ====================
// Lists every parent / standalone job currently being delivered. Each row
// opens that job's customer-facing /track/<job_id> GPS view in a new tab —
// same surface admins share with clients via tracking link emails.
const ClientGpsTracking = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/client/active-tracking`, getClientAuth());
        setJobs(Array.isArray(res.data) ? res.data : []);
      } catch {
        toast.error('Failed to load active tracking');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatStatus = (s) => (s || '').replace(/_/g, ' ');
  const statusBadgeClass = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'in_progress' || v === 'assigned') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (v === 'open') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 size={24} className="animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">GPS Tracking</h1>
        <p className="text-sm text-gray-500">Live tracking pages for jobs currently being delivered.</p>
      </div>

      {jobs.length === 0 ? (
        <Card className="p-12 border-0 shadow-sm text-center" data-testid="gps-tracking-empty">
          <MapPin size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No active jobs right now</p>
          <p className="text-xs text-gray-300 mt-1">
            Tracking pages appear here as soon as a delivery team starts a job.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <Card
              key={j.id}
              className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => window.open(`/track/${j.id}`, '_blank', 'noopener,noreferrer')}
              data-testid={`gps-tracking-row-${j.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900 truncate">{j.title}</p>
                    {j.job_number != null && (
                      <span className="text-xs text-gray-400">#{String(j.job_number).padStart(4, '0')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusBadgeClass(j.status)}`}>
                      {formatStatus(j.status)}
                    </span>
                    {j.is_split_parent && (
                      <span className="inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200">
                        Multi-zone
                      </span>
                    )}
                    {j.location && (
                      <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                        <MapPin size={11} /> {j.location}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/track/${j.id}`, '_blank', 'noopener,noreferrer');
                  }}
                  className="border-purple-300 text-purple-600 hover:bg-purple-50 shrink-0"
                  data-testid={`gps-tracking-open-${j.id}`}
                >
                  <MapPin size={14} className="mr-1.5" />
                  Open Tracking
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== REPORTS ====================
// Job-centric report view. Lists every parent / standalone job owned by the
// client (any status). Clicking a row navigates to /client/reports/job/:id
// where we render a job-detail-style report with stats, the delivery zone
// map, child-jobs overview, delivery progress and admin-curated photos.
const ClientReports = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        // /client/jobs already filters out child jobs (parent_job_id set).
        // We further narrow to the 3 customer-relevant statuses — open,
        // in_progress, completed. pending_approval / rejected / archived /
        // paid jobs are admin-side states and shouldn't surface as
        // browseable reports on the client portal.
        const res = await axios.get(`${API}/api/client/jobs`, getClientAuth());
        const all = Array.isArray(res.data) ? res.data : [];
        const REPORTABLE_STATUSES = new Set(['open', 'in_progress', 'completed']);
        setJobs(all.filter((j) => REPORTABLE_STATUSES.has((j.status || '').toLowerCase())));
      } catch {
        toast.error('Failed to load jobs');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatStatus = (s) => (s || '').replace(/_/g, ' ');
  const statusBadgeClass = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'completed' || v === 'paid' || v === 'approved') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (v === 'in_progress' || v === 'assigned') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (v === 'open') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 size={24} className="animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Reports</h1>

      {jobs.length === 0 ? (
        <Card className="p-12 border-0 shadow-sm text-center">
          <FileText size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No jobs available yet</p>
          <p className="text-xs text-gray-300 mt-1">
            Once a delivery job is created for you, it will appear here as a clickable report.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <Card
              key={j.id}
              className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/client/reports/job/${j.id}`)}
              data-testid={`client-report-job-row-${j.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900 truncate">{j.title}</p>
                    {j.job_number && (
                      <span className="text-xs text-gray-400">#{String(j.job_number).padStart(4, '0')}</span>
                    )}
                  </div>
                  <div className="mt-1">
                    <span
                      className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusBadgeClass(j.status)}`}
                    >
                      {formatStatus(j.status)}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xl font-bold text-purple-600">{j.deliveries || 0}</p>
                  <p className="text-xs text-gray-400">deliveries</p>
                </div>
                <ChevronRight size={18} className="text-gray-300 shrink-0" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== JOB REPORT (single parent job detail) ====================
// Modelled after the admin Job Details modal. Pulls /client/job-reports/:id
// which returns: parent header + aggregated child-progress + admin-curated
// photos (the photos an admin explicitly ticked in the manual report builder).
const ClientJobReport = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/client/job-reports/${jobId}`, getClientAuth());
        if (active) setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load report');
        navigate('/client/reports');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetch();
    return () => { active = false; };
  }, [jobId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-purple-500" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <JobReportView
      data={data}
      onBack={() => navigate('/client/reports')}
      backLabel="Back to Reports"
      testId="client-job-report"
    />
  );
};

// ==================== REQUEST JOB ====================

// Email Settings — client-managed CC recipients for daily update emails
const ClientEmailSettings = () => {
  const [ccList, setCcList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/client/daily-update-cc`, getClientAuth());
        setCcList(res.data?.cc_emails || []);
      } catch { toast.error('Failed to load CC recipients'); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/client/daily-update-cc`, { cc_emails: ccList }, getClientAuth());
      toast.success('CC recipients saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save CC recipients');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="client-email-settings">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Email Settings</h1>
      <p className="text-sm text-gray-500 mb-6">Manage who else receives your delivery update emails.</p>
      <Card className="p-6 border-0 shadow-sm">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Mail size={16} className="text-purple-600" /> Daily Update CC Recipients
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          These addresses will be CC'd on your daily delivery update email. They won't receive any other emails.
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-purple-500" /></div>
        ) : (
          <>
            <DailyCcEditor ccList={ccList} onChange={setCcList} disabled={saving} />
            <div className="flex justify-end mt-4">
              <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="client-cc-save-btn">
                {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                Save
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const ClientRequestJob = () => {
  const [form, setForm] = useState({ title: '', description: '', location: '', estimated_deliveries: '', preferred_date: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Map drawing state
  const [zonePoints, setZonePoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 29.7604, lng: -95.3698 });
  const [centerTrigger, setCenterTrigger] = useState(0);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/client/job-requests`, getClientAuth());
        setRequests(res.data);
      } catch {}
      finally { setLoadingRequests(false); }
    };
    fetch();
  }, []);

  // Map drawing handlers
  const handleAddPoint = useCallback((lat, lng) => {
    setZonePoints(prev => [...prev, [lat, lng]]);
  }, []);

  const handleDragPoint = useCallback((index, lat, lng) => {
    setZonePoints(prev => {
      const updated = [...prev];
      updated[index] = [lat, lng];
      return updated;
    });
  }, []);

  const handleInsertPoint = useCallback((afterIndex, position) => {
    setZonePoints(prev => {
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, [position.lat, position.lng]);
      return updated;
    });
  }, []);

  const handleClosePolygon = useCallback(() => {
    if (zonePoints.length >= 3) {
      setIsDrawing(false);
    }
  }, [zonePoints.length]);

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setZonePoints([]);
  };

  const handleClearZone = () => {
    setIsDrawing(false);
    setZonePoints([]);
  };

  // Address search to center the map
  const handleSearchAddress = async () => {
    if (!searchAddress.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchAddress)}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const loc = data.results[0].geometry.location;
        setMapCenter({ lat: loc.lat, lng: loc.lng });
        setCenterTrigger(prev => prev + 1);
        // Auto-fill location field
        setForm(prev => ({ ...prev, location: data.results[0].formatted_address }));
      } else {
        toast.error('Address not found');
      }
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Please enter a job title'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_deliveries: form.estimated_deliveries ? parseInt(form.estimated_deliveries) : null,
        delivery_boundary: zonePoints.length >= 3 ? zonePoints : null
      };
      await axios.post(`${API}/api/client/job-requests`, payload, getClientAuth());
      toast.success('Job request submitted!');
      setForm({ title: '', description: '', location: '', estimated_deliveries: '', preferred_date: '', notes: '' });
      setZonePoints([]);
      setIsDrawing(false);
      // Refresh requests
      const res = await axios.get(`${API}/api/client/job-requests`, getClientAuth());
      setRequests(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const hasZone = zonePoints.length >= 3 && !isDrawing;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Request a Delivery Job</h1>

      <Card className="p-6 border-0 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Job Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Spring Flyer Campaign" data-testid="request-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Estimated Deliveries</Label>
              <Input type="number" value={form.estimated_deliveries} onChange={e => setForm({...form, estimated_deliveries: e.target.value})} placeholder="e.g., 500" data-testid="request-deliveries" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Preferred Date</Label>
              <Input type="date" value={form.preferred_date} onChange={e => setForm({...form, preferred_date: e.target.value})} data-testid="request-date" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Location / Area</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Auto-filled from map search" data-testid="request-location" />
            </div>
          </div>

          {/* Map Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                <MapPin size={15} className="text-purple-500" />
                Delivery Zone (optional)
              </Label>
              <div className="flex items-center gap-2">
                {!isDrawing && zonePoints.length === 0 && (
                  <Button type="button" size="sm" variant="outline" onClick={handleStartDrawing} className="text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="start-drawing-btn">
                    <Pencil size={14} className="mr-1" /> Draw Zone
                  </Button>
                )}
                {isDrawing && (
                  <p className="text-xs text-purple-600 font-medium animate-pulse">
                    Click on the map to place points. Click the green marker to close.
                  </p>
                )}
                {hasZone && (
                  <>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle size={12} className="mr-1" /> Zone drawn ({zonePoints.length} points)
                    </Badge>
                    <Button type="button" size="sm" variant="outline" onClick={handleClearZone} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="clear-zone-btn">
                      <Trash2 size={14} className="mr-1" /> Clear
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Address Search */}
            <div className="flex gap-2">
              <Input
                value={searchAddress}
                onChange={e => setSearchAddress(e.target.value)}
                placeholder="Search address to center map..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearchAddress(); } }}
                className="flex-1"
                data-testid="map-search-input"
              />
              <Button type="button" size="sm" onClick={handleSearchAddress} disabled={searching} variant="outline" data-testid="map-search-btn">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </Button>
            </div>

            {/* Map */}
            <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 420 }} data-testid="delivery-zone-map">
              <DeliveryZoneMap
                initialCenter={mapCenter}
                initialZoom={14}
                isDrawing={isDrawing}
                zonePoints={zonePoints}
                onAddPoint={handleAddPoint}
                onDragPoint={handleDragPoint}
                onInsertPoint={handleInsertPoint}
                onClosePolygon={handleClosePolygon}
                centerTrigger={centerTrigger}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe the delivery requirements..." rows={3} data-testid="request-description" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Additional Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Any special instructions..." rows={2} data-testid="request-notes" />
          </div>
          <Button type="submit" disabled={submitting} className="bg-purple-600 hover:bg-purple-500" data-testid="submit-request">
            {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
            Submit Request
          </Button>
        </form>
      </Card>

      {/* Previous Requests */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Requests</h2>
        {loadingRequests ? (
          <div className="flex justify-center p-8"><Loader2 size={20} className="animate-spin text-purple-400" /></div>
        ) : requests.length > 0 ? (
          <div className="space-y-2">
            {requests.map(r => (
              <Card key={r.id} className="p-4 border-0 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{r.title}</p>
                      {r.delivery_boundary && r.delivery_boundary.length >= 3 && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs"><MapPin size={10} className="mr-0.5" /> Zone</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                      {r.location && <span>{r.location}</span>}
                      {r.estimated_deliveries && <span>{r.estimated_deliveries} deliveries</span>}
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No requests submitted yet</p>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENT PORTAL LAYOUT ====================
const ClientPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // True when this tab was opened from the Admin → Client Portal button.
  // Drives the amber "Admin preview" banner above the main content so the
  // admin doesn't forget they're looking at the customer view.
  const [isAdminPreview, setIsAdminPreview] = useState(false);

  useEffect(() => {
    try {
      setIsAdminPreview(sessionStorage.getItem('client_portal_from_admin') === '1');
    } catch (_) {}
    const auth = getClientAuth();
    if (!auth) { navigate('/client/login'); return; }
    const stored = localStorage.getItem('client_user');
    if (stored) setUser(JSON.parse(stored));
    // Verify token
    axios.get(`${API}/api/client/auth/me`, auth).catch(() => {
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_user');
      navigate('/client/login');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    navigate('/client/login');
    toast.success('Logged out');
  };

  const navItems = [
    { path: '/client/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/client/jobs', icon: Briefcase, label: 'My Jobs' },
    { path: '/client/gps-tracking', icon: MapPin, label: 'GPS Tracking' },
    { path: '/client/reports', icon: FileText, label: 'Reports' },
    { path: '/client/request-job', icon: PlusCircle, label: 'Request Job' },
    { path: '/client/email-settings', icon: Mail, label: 'Email Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r shadow-sm">
        <div className="p-5 border-b">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <Package size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Client Portal</p>
              <p className="text-xs text-gray-400 truncate">{user?.company || user?.name || ''}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold">
              {(user?.name || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} className="w-full text-gray-500 hover:text-red-600 hover:border-red-200">
            <LogOut size={14} className="mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={20} className="text-purple-600" />
          <span className="font-bold text-gray-900 text-sm">Client Portal</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-64 bg-white shadow-xl p-4 pt-16" onClick={e => e.stopPropagation()}>
            <nav className="space-y-1">
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-600'}`}
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-6 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleLogout} className="w-full text-gray-500">
                <LogOut size={14} className="mr-2" /> Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto lg:pt-0 pt-16">
        {isAdminPreview && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2"
            data-testid="admin-preview-banner"
          >
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 text-amber-900">
                <Shield size={14} className="shrink-0 text-amber-700" />
                <span>
                  <span className="font-semibold">Admin preview</span> — you're viewing the customer-facing portal. Close this tab to return to the admin dashboard.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  try { sessionStorage.removeItem('client_portal_from_admin'); } catch (_) {}
                  setIsAdminPreview(false);
                }}
                className="text-amber-700 hover:text-amber-900 text-xs underline shrink-0"
                data-testid="admin-preview-banner-dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <div className="max-w-5xl mx-auto p-4 lg:p-8">
          <Routes>
            <Route path="dashboard" element={<ClientDashboardHome />} />
            <Route path="jobs" element={<ClientJobsList />} />
            <Route path="jobs/:jobId" element={<ClientJobDetail />} />
            <Route path="gps-tracking" element={<ClientGpsTracking />} />
            <Route path="reports" element={<ClientReports />} />
            <Route path="reports/job/:jobId" element={<ClientJobReport />} />
            <Route path="request-job" element={<ClientRequestJob />} />
            <Route path="email-settings" element={<ClientEmailSettings />} />
            <Route path="*" element={<ClientDashboardHome />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default ClientPortal;
